import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * `<variant-stock-notice>` — keeps the Prusa per-variant "this configuration is
 * out of stock" notice in sync with the selected variant.
 *
 * Which variants get a notice, and what it says, lives entirely in
 * snippets/prusa-variant-stock-notice.liquid and is resolved server-side from
 * `selected_or_first_available_variant`. First paint — including a `?variant=`
 * deep link — is therefore correct with no JS at all, and there is no second
 * copy of the rules in JS to drift out of sync with the Liquid ones.
 *
 * On a variant change we take the freshly rendered notice out of the section
 * HTML the variant picker has already fetched and morph this element's children
 * to match, the same approach as `assets/prusa-prev-gen-callout.js` and
 * `assets/product-inventory.js`. No full page reload.
 *
 * This element is rendered on every variant of a gated product, EMPTY and
 * `hidden` when the selected variant is fine. It has to be: the shopper usually
 * lands on the default (in-stock) variant, so without a mount already in the
 * document there would be nothing to morph and the notice could never appear.
 * The notice's markup, copy and `<style>` are children of the mount, so an empty
 * mount costs the page nothing and carries no styling of its own.
 */
class VariantStockNotice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateNotice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateNotice);
  }

  /**
   * Updates the notice to reflect the newly selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateNotice = (event) => {
    // Combined listing: a different product was loaded, so adopt its id to keep
    // matching. Otherwise ignore updates dispatched for a different product.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newNotice = event.detail.data.html.querySelector('variant-stock-notice');

    // No mount in the new HTML at all means the newly loaded product is not
    // gated in — empty out rather than leave a stale out-of-stock warning on a
    // product it does not apply to.
    if (!newNotice) {
      this.replaceChildren();
      this.hidden = true;
      return;
    }

    morph(this, newNotice, { childrenOnly: true });

    // The snippet emits children only for the variants that actually have a
    // notice, so presence of an element child is the authoritative signal.
    this.hidden = this.childElementCount === 0;
  };
}

if (!customElements.get('variant-stock-notice')) {
  customElements.define('variant-stock-notice', VariantStockNotice);
}
